import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '../Icons';
import { useLanguage } from '../../contexts/LanguageContext';
import ActionPlan from './ActionPlan';
import ElementVisualPreview from './ElementVisualPreview';
import { W3CPerformanceInsights } from '../sections/W3CPerformanceInsights';
import AdvancedDiagnostics from '../sections/AdvancedDiagnostics';
import LCPDiagnosticsPanel from './LCPDiagnosticsPanel';
import TTFBDiagnosticsPanel from './TTFBDiagnosticsPanel';
import RTTDiagnosticsPanel from './RTTDiagnosticsPanel';
import INPDiagnosticsPanel from './INPDiagnosticsPanel';
import { generateDiagnosticsHTML, downloadHTML } from '../../utils/diagnosticsExport';
import { generateMockupHTML, generateAuditHTML, downloadHTMLFile } from '../../utils/diagnosticsExportStyles';
import { generateClsTasks, hasDeepTestAccess } from '../../utils/quickAudit';
import { deriveCanonicalFinding } from '../../utils/canonicalFinding';
import DevicePriorityRanking from './DevicePriorityRanking';
import DiagnosticEvidenceView from './DiagnosticEvidenceView';
import AuditEvidenceContractView from './AuditEvidenceContractView';
import { buildPanelEvidenceContract } from '../../utils/auditEvidencePanelPayload';
import { secureFetch } from '../../services/api';

const severityColors = {
  high: 'bg-red-500/20 text-red-400 border-red-500/30',
  medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
};

const severityLabels = {
  high: { en: 'High', cs: 'Vysoká' },
  medium: { en: 'Medium', cs: 'Střední' },
  low: { en: 'Low', cs: 'Nízká' }
};

const truncateUrl = (url) => {
  if (!url) return '';
  return url;
};

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const DIAGNOSTICS_CACHE = new Map();
const DIAGNOSTICS_CACHE_TTL_MS = 10 * 60 * 1000;
const DIAGNOSTICS_CACHE_MAX = 50;
function getDiagCacheKey(url, strategy) {
  return `${(url || '').trim().toLowerCase().replace(/\/$/, '')}::${strategy || 'desktop'}`;
}
function pruneDiagnosticsCache() {
  const now = Date.now();
  for (const [k, v] of DIAGNOSTICS_CACHE) {
    if (!v || now - v.fetchedAt >= DIAGNOSTICS_CACHE_TTL_MS) DIAGNOSTICS_CACHE.delete(k);
  }
  while (DIAGNOSTICS_CACHE.size > DIAGNOSTICS_CACHE_MAX) {
    let oldestKey = null;
    let oldestAt = Infinity;
    for (const [k, v] of DIAGNOSTICS_CACHE) {
      if (v.fetchedAt < oldestAt) { oldestAt = v.fetchedAt; oldestKey = k; }
    }
    if (oldestKey) DIAGNOSTICS_CACHE.delete(oldestKey); else break;
  }
}

export const DiagnosticsPanel = ({ url, metric, strategy = 'desktop', onClose, user, metricValue, selectedDevice = null }) => {
  const cacheKey = getDiagCacheKey(url, strategy);
  const cached = DIAGNOSTICS_CACHE.get(cacheKey);
  const cacheFresh = cached && (Date.now() - cached.fetchedAt < DIAGNOSTICS_CACHE_TTL_MS);
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(!cacheFresh);
  const [error, setError] = useState(null);
  const [diagnostics, setDiagnostics] = useState(cacheFresh ? cached.diagnostics : null);
  const [savingsRecommendations, setSavingsRecommendations] = useState(cacheFresh ? cached.recommendations : []);
  const [savingsSummary, setSavingsSummary] = useState(cacheFresh ? cached.summary : null);
  const [recommendationsSource, setRecommendationsSource] = useState(cacheFresh ? cached.source : null);
  const [snapshotFetchedAt, setSnapshotFetchedAt] = useState(cacheFresh ? cached.snapshotFetchedAt : null);
  const [forceReloadCounter, setForceReloadCounter] = useState(0);
  const [isRecomputing, setIsRecomputing] = useState(false);
  const [isStableMeasuring, setIsStableMeasuring] = useState(false);
  const stableRunsRef = useRef(1);
  const [deepCLSLoading, setDeepCLSLoading] = useState(false);
  const [deepCLSResults, setDeepCLSResults] = useState(null);
  const [skipCmp, setSkipCmp] = useState(false);
  const [captureVisuals, setCaptureVisuals] = useState(false);
  const [multiPageLoading, setMultiPageLoading] = useState(false);
  const [multiPageResults, setMultiPageResults] = useState(null);
  const [multiPageProgress, setMultiPageProgress] = useState(null);
  const [expandedPageIndex, setExpandedPageIndex] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const saveMenuRef = useRef(null);

  useEffect(() => {
    if (!showSaveMenu) return undefined;
    const handler = (e) => {
      if (saveMenuRef.current && !saveMenuRef.current.contains(e.target)) {
        setShowSaveMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSaveMenu]);
  const [deepINPResults, setDeepINPResults] = useState(null);
  const [inpCompareResults, setInpCompareResults] = useState(null);
  const [multiPageINPResults, setMultiPageINPResults] = useState(null);
  const [multiPageLCPResults, setMultiPageLCPResults] = useState(null);
  const [deepLCPResults, setDeepLCPResults] = useState(null);
  const [deepTTFBResults, setDeepTTFBResults] = useState(null);
  const [devicePriority, setDevicePriority] = useState(null);
  const [collectedScreenshots, setCollectedScreenshots] = useState([]);
  const panelRef = useRef(null);
  const diagnosticsPromiseRef = useRef(null);
  
  const isPremium = user?.tier === 'premium' || user?.tier === 'pro' || user?.is_admin;
  const hasDeepAccess = hasDeepTestAccess(user);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  const generateDeveloperTasks = () => generateClsTasks(deepCLSResults, language);
  const measuredDeepRunForMetric = () => ({
    INP: deepINPResults,
    CLS: deepCLSResults,
    LCP: deepLCPResults,
    TTFB: deepTTFBResults
  })[String(metric || '').toUpperCase()] || null;
  // Report-level provenance must belong to the audited metric. A different
  // deep run (e.g. LCP in a TTFB report) cannot attest to this headline.
  const measurementProvenanceFor = (diagnosticsValue = diagnostics) => {
    const metricRun = measuredDeepRunForMetric();
    // Missing attestation on an existing run is unknown, not an invitation
    // to borrow the separate PSI diagnostic request's collector.
    return metricRun
      ? metricRun.measurementProvenance || null
      : diagnosticsValue?.measurementProvenance || null;
  };

  // Export header must state the device the deep run actually measured, not
  // the dashboard context device (which once mislabeled a mobile run as
  // "Tablet"). Deep-run results carry their own `device`; prefer the one
  // matching the audited metric, then fall back to the dashboard selection.
  const exportDevice = () => {
    const m = String(metric || '').toUpperCase();
    const deepDeviceByMetric =
      (m === 'INP' && deepINPResults?.device) ||
      (m === 'CLS' && deepCLSResults?.device) ||
      (m === 'LCP' && deepLCPResults?.device) ||
      (m === 'TTFB' && deepTTFBResults?.device) ||
      null;
    return deepDeviceByMetric || deepINPResults?.device || selectedDevice || strategy;
  };

  const buildVariantPayload = (overrides = {}) => {
    const tasks = generateDeveloperTasks();
    const screenshots = deepCLSResults?.elements
      ?.filter(el => el.screenshot)
      ?.map(el => ({ data: el.screenshot, caption: el.selector })) || [];

    const payload = {
      url,
      metric: overrides.metric ?? metric,
      value: overrides.value !== undefined ? overrides.value : metricValue ?? deepCLSResults?.cls,
      fieldValue: overrides.fieldValue !== undefined ? overrides.fieldValue : metricValue ?? null,
      diagnostics: overrides.diagnostics !== undefined ? overrides.diagnostics : diagnostics,
      deepCLSResults,
      multiPageResults,
      deepINPResults,
      inpCompareResults,
      multiPageINPResults,
      multiPageLCPResults,
      deepLCPResults,
      deepTTFBResults,
      screenshots: [...screenshots, ...collectedScreenshots],
      tasks,
      language,
      device: exportDevice(),
      fieldDevice: selectedDevice || strategy || null,
      savingsRecommendations: overrides.savingsRecommendations !== undefined ? overrides.savingsRecommendations : savingsRecommendations,
      savingsSummary: overrides.savingsSummary !== undefined ? overrides.savingsSummary : savingsSummary,
      devicePriority,
      measurementProvenance: measurementProvenanceFor(
        overrides.diagnostics !== undefined ? overrides.diagnostics : diagnostics
      ),
      // Follow-up schedule anchor: exports generated from the panel reflect a
      // just-completed measurement, so the audit date is "now" at export time.
      auditDate: new Date()
    };
    return { ...payload, evidenceContract: buildPanelEvidenceContract(payload) };
  };

  const generateVariantHTML = (variant = 'standard', overrides = {}, preparedPayload = null) => {
    const payload = preparedPayload || buildVariantPayload(overrides);
    if (variant === 'dashboard') {
      return generateMockupHTML(payload);
    }
    if (variant === 'audit') {
      return generateAuditHTML(payload);
    }
    return generateDiagnosticsHTML({
      ...payload,
      includeW3C: true,
      includeAdvanced: true,
      includeActionPlan: true,
    });
  };

  const generateCurrentHTML = (overrides = {}) => generateVariantHTML('standard', overrides);

  const handleExportHTML = async () => {
    setExporting('html');
    try {
      const html = generateCurrentHTML();
      let domain = 'report';
      if (url) {
        try {
          const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
          domain = new URL(normalized).hostname.replace(/\./g, '_');
        } catch (_) {
          domain = String(url).replace(/[^a-z0-9]+/gi, '_').slice(0, 60) || 'report';
        }
      }
      const dev = String(exportDevice() || '').toLowerCase().replace(/[^a-z]/g, '');
      const filename = `diagnostics_${metric}${dev ? `_${dev}` : ''}_${domain}_${new Date().toISOString().split('T')[0]}_${Date.now()}.html`;
      downloadHTML(html, filename);
    } catch (err) {
      console.error('HTML export error:', err);
    } finally {
      setExporting(null);
    }
  };

  const exportPayload = () => buildVariantPayload();

  const buildFilename = (suffix) => {
    let domain = 'report';
    if (url) {
      try {
        const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
        domain = new URL(normalized).hostname.replace(/\./g, '_');
      } catch (_) {
        domain = String(url).replace(/[^a-z0-9]+/gi, '_').slice(0, 60) || 'report';
      }
    }
    // Include the measured device so exports are distinguishable per device
    // (e.g. audit_INP_mobile_… vs audit_INP_tablet_…).
    const dev = String(exportDevice() || '').toLowerCase().replace(/[^a-z]/g, '');
    return `${suffix}_${metric}${dev ? `_${dev}` : ''}_${domain}_${new Date().toISOString().split('T')[0]}_${Date.now()}.html`;
  };

  const fetchAiNarrative = async (variant, payload) => {
    if (!user) return null;
    // Client-side short-circuit: skip the API call when the tier is clearly
    // ineligible. Saves a round-trip + a usage row. Server still re-validates.
    const aiFlag = typeof user.tier === 'object' ? user.tier?.allowAiNarrative : undefined;
    if (aiFlag === false && !user.is_admin) return null;
    if (aiFlag !== true && !user.is_admin) {
      const tierSlug = typeof user.tier === 'string' ? user.tier : (user.tier?.slug || '');
      const ELIGIBLE = new Set(['business', 'enterprise']);
      if (tierSlug && !ELIGIBLE.has(tierSlug)) return null;
    }
    try {
      const csrfRes = await fetch('/api/csrf-token', { credentials: 'include' });
      const { token: csrfToken } = await csrfRes.json();
      const res = await fetch('/api/exports/ai-narrative', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrfToken },
        body: JSON.stringify({
          variant,
          url: payload.url,
          metric: payload.metric,
          value: payload.value,
          language: payload.language,
          device: payload.device,
          diagnosticsSummary: {
            culpritsCount: Array.isArray(payload.diagnostics?.culprits) ? payload.diagnostics.culprits.length : 0,
            recommendationsCount: payload.savingsRecommendations?.length || 0,
            multiPage: !!(payload.multiPageLCPResults || payload.multiPageINPResults || payload.multiPageResults),
            hasDeepResults: !!(payload.deepLCPResults || payload.deepCLSResults || payload.deepINPResults),
            estimatedSavingsMs: payload.savingsSummary?.totalMs || null
          },
          // Causality guardrail: deterministic conclusion the AI must
          // interpret without introducing or strengthening a cause.
          canonicalFinding: (() => {
            try { return deriveCanonicalFinding(payload); } catch { return null; }
          })()
        })
      });
      if (!res.ok) return null;
      const json = await res.json();
      return json?.success ? json.narrative : null;
    } catch (err) {
      console.warn('AI narrative fetch failed (silent fallback):', err?.message);
      return null;
    }
  };

  const handleExportMockup = async () => {
    setExporting('mockup');
    try {
      const payload = exportPayload();
      const aiNarrative = await fetchAiNarrative('dashboard', payload);
      const html = generateMockupHTML({ ...payload, aiNarrative });
      downloadHTMLFile(html, buildFilename('dashboard'));
    } catch (err) {
      console.error('Mockup export error:', err);
    } finally {
      setExporting(null);
    }
  };

  const handleExportAudit = async () => {
    setExporting('audit');
    try {
      const payload = exportPayload();
      const aiNarrative = await fetchAiNarrative('audit', payload);
      const html = generateAuditHTML({ ...payload, aiNarrative });
      downloadHTMLFile(html, buildFilename('audit'));
    } catch (err) {
      console.error('Audit export error:', err);
    } finally {
      setExporting(null);
    }
  };

  // Unified CWV export: the final step of the deep workflow (single-page deep
  // run(s) → optional multi-page sweep → this). Headline follows priority #1
  // from the device ranking; every deep layer held by the panel renders in
  // ONE report (presence-gated via unified:true).
  const unifiedDeepCount = [deepINPResults, deepCLSResults, deepLCPResults].filter(Boolean).length;
  const handleExportUnifiedAudit = async () => {
    setExporting('cwv');
    try {
      const top = Array.isArray(devicePriority?.items)
        ? devicePriority.items.find(i => Number(i.score) > 0)
        : null;
      const payload = {
        ...buildVariantPayload(top ? {
          metric: String(top.metric).toUpperCase(), value: top.p75 ?? metricValue,
          fieldValue: top.p75 ?? null
        } : {}),
        unified: true,
        // Headline = priority #1 metric when ranking exists; contract shares
        // that headline's field scope without changing any deep-run device.
      };
      const aiNarrative = await fetchAiNarrative('audit', payload);
      const html = generateAuditHTML({ ...payload, aiNarrative });
      downloadHTMLFile(html, buildFilename('audit_CWV'));
    } catch (err) {
      console.error('Unified CWV audit export error:', err);
    } finally {
      setExporting(null);
    }
  };

  const ensureDiagnosticsReady = async () => {
    let diag = diagnostics;
    let recs = savingsRecommendations;
    let summary = savingsSummary;

    const cached = url ? DIAGNOSTICS_CACHE.get(getDiagCacheKey(url, strategy)) : null;
    const hasFreshState = !loading && (recs?.length > 0 || summary);
    const hasFreshCache = cached && Date.now() - cached.fetchedAt < DIAGNOSTICS_CACHE_TTL_MS
      && (cached.recommendations?.length > 0 || cached.summary);

    if (!hasFreshState && diagnosticsPromiseRef.current) {
      try {
        const result = await diagnosticsPromiseRef.current;
        if (result) {
          diag = result.diagnostics ?? diag;
          recs = result.recommendations ?? recs;
          summary = result.summary ?? summary;
        }
      } catch (_) {
        // fall through to cache fallback below
      }
    }

    if ((!recs || recs.length === 0) && !summary && hasFreshCache) {
      diag = cached.diagnostics;
      recs = cached.recommendations || [];
      summary = cached.summary || null;
    }

    return { diagnostics: diag, savingsRecommendations: recs || [], savingsSummary: summary || null };
  };

  const variantLabelFor = (variant) => {
    const labels = {
      standard: language === 'cs' ? 'standardní' : 'standard',
      dashboard: 'dashboard',
      audit: 'audit'
    };
    return labels[variant] || labels.standard;
  };

  const handleSaveHTMLToCloud = async (variant = 'standard') => {
    if (!user) return;
    setShowSaveMenu(false);
    // Mirror the wait condition inside ensureDiagnosticsReady: we only await
    // when there is no fresh in-memory state AND a diagnostics fetch is still
    // in flight. The promise ref is cleared in its own finally block, so a
    // resolved-but-empty diagnostics result will not trigger a false wait.
    const hasFreshState = !loading && (savingsRecommendations?.length > 0 || savingsSummary);
    const willWaitForDiagnostics = !hasFreshState && Boolean(diagnosticsPromiseRef.current);
    setExporting(willWaitForDiagnostics ? 'cloud-waiting' : 'cloud');
    try {
      const ready = await ensureDiagnosticsReady();
      if (willWaitForDiagnostics) setExporting('cloud');

      const variantSuffix = variantLabelFor(variant);
      const measurementProvenance = measurementProvenanceFor(ready.diagnostics);
      const preparedPayload = buildVariantPayload(ready);

      const saveRes = await secureFetch('/api/lab-audits/save-analysis', {
        method: 'POST',
        body: JSON.stringify({
          url: url || '',
          title: `${metric} diagnostika (${variantSuffix}): ${url || ''}`,
          analysisData: {
            metrics: { [metric]: { p75: metricValue } },
            diagnosticMetric: metric,
            reportVariant: variant,
          },
          diagnosticsData: {
            diagnostics: ready.diagnostics,
            deepCLSResults,
            deepINPResults,
            deepLCPResults,
            deepTTFBResults,
             measurementProvenance,
             evidenceContract: preparedPayload.evidenceContract
          },
           evidenceContract: preparedPayload.evidenceContract,
          measurementProvenance,
          device: selectedDevice || strategy || 'desktop',
          dataSource: 'diagnostics'
        })
      });
      if (!saveRes.ok) throw new Error('Save failed');
      const saveData = await saveRes.json();

      const html = generateVariantHTML(variant, {}, preparedPayload);
      const htmlRes = await secureFetch('/api/lab-audits/save-analysis-html', {
        method: 'POST',
        body: JSON.stringify({ analysisId: saveData.id, html, variant })
      });
      if (!htmlRes.ok) throw new Error('HTML save failed');

      setExporting('cloud-done');
      setTimeout(() => setExporting(null), 2500);
    } catch (err) {
      console.error('Cloud save error:', err);
      setExporting(null);
    }
  };

  const handleSaveAllVariantsToCloud = async () => {
    if (!user) return;
    setShowSaveMenu(false);

    const variants = ['standard', 'dashboard', 'audit'];
    const total = variants.length;

    const hasFreshState = !loading && (savingsRecommendations?.length > 0 || savingsSummary);
    const willWaitForDiagnostics = !hasFreshState && Boolean(diagnosticsPromiseRef.current);
    setExporting(willWaitForDiagnostics ? 'cloud-waiting' : 'cloud-bulk');
    setBulkProgress({ current: 0, total, failed: [] });

    try {
      const ready = await ensureDiagnosticsReady();
      if (willWaitForDiagnostics) setExporting('cloud-bulk');

      const allLabel = language === 'cs' ? 'všechny varianty' : 'all variants';
      const measurementProvenance = measurementProvenanceFor(ready.diagnostics);
      const preparedPayload = buildVariantPayload(ready);
      const saveRes = await secureFetch('/api/lab-audits/save-analysis', {
        method: 'POST',
        body: JSON.stringify({
          url: url || '',
          title: `${metric} diagnostika (${allLabel}): ${url || ''}`,
          analysisData: {
            metrics: { [metric]: { p75: metricValue } },
            diagnosticMetric: metric,
            reportVariant: 'all',
          },
          diagnosticsData: {
            diagnostics: ready.diagnostics,
            deepCLSResults,
            deepINPResults,
            deepLCPResults,
            deepTTFBResults,
             measurementProvenance,
             evidenceContract: preparedPayload.evidenceContract
          },
           evidenceContract: preparedPayload.evidenceContract,
          measurementProvenance,
          device: selectedDevice || strategy || 'desktop',
          dataSource: 'diagnostics'
        })
      });
      if (!saveRes.ok) throw new Error('Save failed');
      const saveData = await saveRes.json();

      const failed = [];
      for (let i = 0; i < variants.length; i += 1) {
        const variant = variants[i];
        setBulkProgress({ current: i + 1, total, failed: [...failed] });
        try {
          const html = generateVariantHTML(variant, {}, preparedPayload);
          const htmlRes = await secureFetch('/api/lab-audits/save-analysis-html', {
            method: 'POST',
            body: JSON.stringify({ analysisId: saveData.id, html, variant })
          });
          if (!htmlRes.ok) {
            failed.push(variant);
            console.error(`Cloud save error for variant ${variant}: HTTP ${htmlRes.status}`);
          }
        } catch (variantErr) {
          failed.push(variant);
          console.error(`Cloud save error for variant ${variant}:`, variantErr);
        }
      }

      setBulkProgress({ current: total, total, failed });
      if (failed.length === total) {
        setExporting(null);
      } else if (failed.length > 0) {
        setExporting('cloud-partial');
        setTimeout(() => { setExporting(null); setBulkProgress(null); }, 4000);
      } else {
        setExporting('cloud-done');
        setTimeout(() => { setExporting(null); setBulkProgress(null); }, 2500);
      }
    } catch (err) {
      console.error('Cloud bulk save error:', err);
      setExporting(null);
      setBulkProgress(null);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    const forceFresh = forceReloadCounter > 0;
    const fetchDiagnostics = async () => {
      if (!url) return null;

      pruneDiagnosticsCache();
      const key = getDiagCacheKey(url, strategy);
      // When the user explicitly asked to recompute, bypass the in-memory
      // cache as well — otherwise we'd happily return the stale snapshot.
      if (!forceFresh) {
        const c = DIAGNOSTICS_CACHE.get(key);
        if (c && Date.now() - c.fetchedAt < DIAGNOSTICS_CACHE_TTL_MS) {
          if (!cancelled) {
            setDiagnostics(c.diagnostics);
            setSavingsRecommendations(c.recommendations || []);
            setSavingsSummary(c.summary || null);
            setRecommendationsSource(c.source || null);
            setSnapshotFetchedAt(c.snapshotFetchedAt || null);
            setLoading(false);
            setError(null);
          }
          return {
            diagnostics: c.diagnostics,
            recommendations: c.recommendations || [],
            summary: c.summary || null
          };
        }
      }

      if (!cancelled) {
        setLoading(true);
        setError(null);
      }

      try {
        const response = await fetch('/api/compare/diagnostics', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            stableRunsRef.current > 1
              ? { url, strategy, force: true, runs: stableRunsRef.current }
              : forceFresh ? { url, strategy, force: true } : { url, strategy }
          ),
          signal: controller.signal
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch diagnostics');
        }
        
        const data = await response.json();
        const recs = Array.isArray(data.recommendations) ? data.recommendations : [];
        const summary = data.recommendationsSummary || null;
        const source = data.recommendationsSource || null;
        const snapshotAt = data.snapshotFetchedAt || null;
        DIAGNOSTICS_CACHE.set(key, {
          diagnostics: data.diagnostics,
          recommendations: recs,
          summary,
          source,
          snapshotFetchedAt: snapshotAt,
          fetchedAt: Date.now()
        });
        pruneDiagnosticsCache();
        if (cancelled) {
          return { diagnostics: data.diagnostics, recommendations: recs, summary };
        }
        setDiagnostics(data.diagnostics);
        setSavingsRecommendations(recs);
        setSavingsSummary(summary);
        setRecommendationsSource(source);
        setSnapshotFetchedAt(snapshotAt);
        return { diagnostics: data.diagnostics, recommendations: recs, summary };
      } catch (err) {
        if (err.name === 'AbortError' || cancelled) return null;
        console.error('Diagnostics error:', err);
        setError(err.message);
        return null;
      } finally {
        stableRunsRef.current = 1;
        if (!cancelled) {
          setLoading(false);
          setIsRecomputing(false);
          setIsStableMeasuring(false);
        }
      }
    };
    
    const promise = fetchDiagnostics();
    diagnosticsPromiseRef.current = promise;
    return () => {
      cancelled = true;
      controller.abort();
      if (diagnosticsPromiseRef.current === promise) {
        diagnosticsPromiseRef.current = null;
      }
    };
  }, [url, strategy, forceReloadCounter]);

  const handleRecompute = () => {
    if (loading || isRecomputing) return;
    if (url) {
      DIAGNOSTICS_CACHE.delete(getDiagCacheKey(url, strategy));
    }
    setIsRecomputing(true);
    setForceReloadCounter((n) => n + 1);
  };

  // Stable measurement: 5 independent PSI runs, the report uses the run with
  // the median TBT and shows min–max ranges (labStability) for key numbers.
  const handleStableMeasure = () => {
    if (loading || isRecomputing || isStableMeasuring) return;
    if (url) {
      DIAGNOSTICS_CACHE.delete(getDiagCacheKey(url, strategy));
    }
    stableRunsRef.current = 5;
    setIsStableMeasuring(true);
    setForceReloadCounter((n) => n + 1);
  };

  const metricKey = metric?.toLowerCase();
  const metricData = diagnostics?.[metricKey];

  const issueExplanations = {
    'images-without-dimensions': {
      cs: {
        why: 'Obrázky bez width a height způsobují posun obsahu při načítání (CLS).',
        fix: 'Přidejte atributy width a height ke každému <img> tagu.'
      },
      en: {
        why: 'Images without width and height cause layout shifts during loading (CLS).',
        fix: 'Add width and height attributes to each <img> tag.'
      }
    },
    'unsized-images': {
      cs: {
        why: 'Obrázky bez definovaných rozměrů způsobují posun obsahu při načítání.',
        fix: 'Přidejte atributy width a height nebo CSS aspect-ratio.'
      },
      en: {
        why: 'Images without defined dimensions cause content shifts during loading.',
        fix: 'Add width and height attributes or CSS aspect-ratio.'
      }
    },
    'render-blocking': {
      cs: {
        why: 'Blokující zdroje zpomalují vykreslení stránky.',
        fix: 'Přidejte async/defer ke skriptům, nebo je přesuňte na konec <body>.'
      },
      en: {
        why: 'Render-blocking resources slow down page rendering.',
        fix: 'Add async/defer to scripts, or move them to end of <body>.'
      }
    },
    'long-tasks': {
      cs: {
        why: 'Dlouhé JavaScriptové úlohy blokují hlavní vlákno a zhoršují INP.',
        fix: 'Rozdělte dlouhé úlohy na menší části pomocí setTimeout nebo requestIdleCallback.'
      },
      en: {
        why: 'Long JavaScript tasks block the main thread and worsen INP.',
        fix: 'Break long tasks into smaller chunks using setTimeout or requestIdleCallback.'
      }
    },
    'third-party': {
      cs: {
        why: 'Skripty třetích stran zpomalují načítání a ovlivňují Core Web Vitals.',
        fix: 'Načtěte tyto skripty asynchronně nebo je odložte pomocí lazy loading.'
      },
      en: {
        why: 'Third-party scripts slow loading and impact Core Web Vitals.',
        fix: 'Load these scripts asynchronously or defer them using lazy loading.'
      }
    }
  };

  const getIssueExplanation = (message) => {
    if (!message) return null;
    const msgLower = message.toLowerCase();
    if (msgLower.includes('obrázky bez') || msgLower.includes('images without') || msgLower.includes('unsized')) {
      return issueExplanations['unsized-images'];
    }
    if (msgLower.includes('blokující') || msgLower.includes('render-blocking')) {
      return issueExplanations['render-blocking'];
    }
    if (msgLower.includes('dlouhé úlohy') || msgLower.includes('long task')) {
      return issueExplanations['long-tasks'];
    }
    if (msgLower.includes('třetí stran') || msgLower.includes('third-party') || msgLower.includes('third party')) {
      return issueExplanations['third-party'];
    }
    return null;
  };

  const getFilenameFromUrl = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      return urlObj.hostname + urlObj.pathname + urlObj.search;
    } catch {
      return url;
    }
  };

  const renderIssue = (issue, index) => {
    if (!issue) return null;
    const severity = issue.severity || 'medium';
    const explanation = getIssueExplanation(issue.message);
    const itemCount = issue.items?.length || 0;
    
    return (
      <div key={index} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
        <div className="flex items-start gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 ${severityColors[severity] || severityColors.medium}`}>
            {severityLabels[severity]?.[language] || severity}
          </span>
          <div className="flex-1">
            <span className="text-sm text-white font-medium">
              {language === 'en' && issue.message_en ? issue.message_en : (issue.message || '')}
            </span>
            {itemCount > 0 && (
              <span className="text-xs text-slate-500 ml-2">({itemCount} {language === 'cs' ? 'položek' : 'items'})</span>
            )}
          </div>
        </div>
        
        {explanation && (
          <div className="mt-2 ml-14 p-2 rounded bg-slate-900/50 border-l-2 border-amber-500/50">
            <p className="text-xs text-slate-300">
              <span className="text-amber-400 font-medium">{language === 'cs' ? 'Proč:' : 'Why:'}</span> {explanation[language]?.why}
            </p>
            <p className="text-xs text-slate-300 mt-1">
              <span className="text-green-400 font-medium">{language === 'cs' ? 'Řešení:' : 'Fix:'}</span> {explanation[language]?.fix}
            </p>
          </div>
        )}
        
        {issue.details && !explanation && (
          <p className="text-xs text-slate-400 mt-2 ml-14">{issue.details}</p>
        )}
        
        {issue.items && issue.items.length > 0 && (
          <div className="mt-2 ml-14">
            <details open={issue.type === 'main-thread'}>
              <summary className="text-[10px] text-slate-500 uppercase mb-1 cursor-pointer hover:text-slate-300">
                {issue.type === 'main-thread'
                  ? (language === 'cs' ? 'Rozložení času:' : 'Time breakdown:')
                  : (language === 'cs' ? 'Dotčené soubory:' : 'Affected files:')} ({issue.items.length})
              </summary>
              <div className="space-y-1 mt-1">
                {(() => {
                  const groupLabels = {
                    scriptEvaluation: { cs: 'Vyhodnocení skriptů', en: 'Script evaluation', color: 'bg-amber-500' },
                    scriptParseCompile: { cs: 'Parsování a kompilace JS', en: 'Script parsing & compilation', color: 'bg-orange-500' },
                    styleLayout: { cs: 'Styly a layout', en: 'Style & layout', color: 'bg-purple-500' },
                    paintCompositeRender: { cs: 'Vykreslování', en: 'Rendering & compositing', color: 'bg-green-500' },
                    garbageCollection: { cs: 'Správa paměti (GC)', en: 'Garbage collection', color: 'bg-red-500' },
                    other: { cs: 'Ostatní', en: 'Other', color: 'bg-slate-500' },
                    parseHTML: { cs: 'Parsování HTML', en: 'HTML parsing', color: 'bg-cyan-500' }
                  };
                  const maxDuration = Math.max(...issue.items.map(it => it.duration || 0), 1);
                  return issue.items.map((item, i) => {
                    if (!item) return null;
                    const filename = item.url ? getFilenameFromUrl(item.url) : null;
                    const meta = item.group && groupLabels[item.group] ? groupLabels[item.group] : null;
                    const groupDisplay = meta
                      ? (meta[language] || item.groupLabel || item.group)
                      : (item.groupLabel || item.group);
                    const barColor = meta?.color || 'bg-cyan-500';
                    const barWidth = item.duration ? Math.max((item.duration / maxDuration) * 100, 4) : 0;
                    return (
                      <div key={i} className="text-xs text-slate-400">
                        {issue.type === 'main-thread' && item.duration ? (
                          <div className="flex items-center gap-2">
                            <span className="w-[140px] sm:w-[180px] text-slate-300 font-medium shrink-0">{groupDisplay}</span>
                            <div className="flex-1 h-4 bg-slate-900/60 rounded overflow-hidden relative">
                              <div className={`h-full ${barColor} rounded opacity-80`} style={{ width: `${barWidth}%` }} />
                            </div>
                            <span className="text-amber-400 font-mono shrink-0 w-[60px] text-right">{item.duration}ms</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 group">
                            <span className="text-cyan-400">•</span>
                            {filename && (
                              <span className="font-mono text-cyan-300 break-all text-[10px]" title={item.url}>{filename}</span>
                            )}
                            {item.entity && <span>{item.entity}</span>}
                            {groupDisplay && <span className="font-medium text-slate-300">{groupDisplay}</span>}
                            {item.duration && <span className="text-amber-400 font-mono">{item.duration}ms</span>}
                            {item.blockingTime && <span className="text-red-400 font-mono">{item.blockingTime}ms</span>}
                            {item.wastedMs && <span className="text-amber-400">-{Math.round(item.wastedMs)}ms</span>}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </details>
          </div>
        )}
      </div>
    );
  };

  const renderLCPDiagnostics = () => {
    const lcp = diagnostics?.lcp;
    if (!lcp) return null;

    return (
      <div className="space-y-4">
        {lcp.element && (
          <div className="p-4 rounded-lg bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border border-cyan-500/30">
            <div className="text-xs uppercase tracking-wider text-cyan-400 mb-2 flex items-center gap-1">
              <Icons.Target size={12} /> {t('metricsDisplay.diagnostics.lcpElement')}
            </div>
            <code className="text-sm text-white bg-slate-900 px-3 py-2 rounded block overflow-x-auto">
              {lcp.element.node || lcp.element.selector || 'N/A'}
            </code>
            {lcp.element.selector && lcp.element.selector !== lcp.element.node && (
              <div className="text-xs text-slate-400 mt-2">
                Selector: <code className="text-cyan-300">{lcp.element.selector}</code>
              </div>
            )}
          </div>
        )}
        
        {lcp.issues?.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-red-400 mb-2 flex items-center gap-1">
              <Icons.AlertTriangle size={12} /> {t('metricsDisplay.diagnostics.issuesFound')}
            </div>
            <div className="space-y-2">
              {lcp.issues.map(renderIssue)}
            </div>
          </div>
        )}

        {user && (
          <div className="mt-4 pt-4 border-t border-slate-700">
            <LCPDiagnosticsPanel url={url} user={user} onResultsChange={(data) => {
              if ('multiPageLCP' in data) setMultiPageLCPResults(data.multiPageLCP);
              if ('deepLCP' in data) setDeepLCPResults(data.deepLCP);
            }} />
            <DiagnosticEvidenceView result={deepLCPResults} metric="LCP" language={language} />
          </div>
        )}
      </div>
    );
  };

  // Generate user-friendly summary for deep CLS analysis
  const renderDeepCLSSummary = (results) => {
    if (!results) return null;
    
    const elements = results.elements || [];
    const recommendations = results.recommendations || [];
    
    // Count causes
    const causeCounts = {};
    elements.forEach(el => {
      const cause = el.cause || 'unknown';
      causeCounts[cause] = (causeCounts[cause] || 0) + 1;
    });
    
    // Find biggest shifter
    const biggestShifter = elements.length > 0 
      ? elements.reduce((max, el) => (el.totalShift || 0) > (max.totalShift || 0) ? el : max, elements[0])
      : null;
    
    // Cause labels for summary
    const causeNames = {
      'consent-banner': { cs: 'cookie consent banner', en: 'cookie consent banner' },
      'font-swap': { cs: 'webfonty', en: 'web fonts' },
      'ad-injection': { cs: 'reklamy', en: 'ads' },
      'unsized-image': { cs: 'obrázky bez rozměrů', en: 'unsized images' },
      'iframe-embed': { cs: 'iframe embedy', en: 'iframe embeds' },
      'video-embed': { cs: 'video embedy', en: 'video embeds' },
      'dynamic-content': { cs: 'dynamický obsah', en: 'dynamic content' },
      'animation': { cs: 'animace', en: 'animations' },
      'image-load': { cs: 'načítání obrázků', en: 'image loading' },
      'unknown': { cs: 'neznámé příčiny', en: 'unknown causes' }
    };
    
    // Build summary parts
    const summaryParts = [];
    
    // Main findings
    if (elements.length === 0) {
      summaryParts.push(language === 'cs' 
        ? 'Nebyly detekovány žádné významné layout shifty.' 
        : 'No significant layout shifts were detected.');
    } else {
      // Biggest shifter
      if (biggestShifter) {
        const selector = biggestShifter.selector || biggestShifter.tagName || 'unknown element';
        const shortSelector = selector;
        summaryParts.push(language === 'cs'
          ? `Největší posun způsobuje "${shortSelector}" (shift: ${biggestShifter.totalShift?.toFixed(3)}).`
          : `The biggest shift is caused by "${shortSelector}" (shift: ${biggestShifter.totalShift?.toFixed(3)}).`);
      }
      
      // Cause summary
      const causeList = Object.entries(causeCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([cause, count]) => {
          const name = causeNames[cause]?.[language] || cause;
          return count > 1 ? `${name} (${count}×)` : name;
        });
      
      if (causeList.length > 0) {
        summaryParts.push(language === 'cs'
          ? `Detekované příčiny: ${causeList.join(', ')}.`
          : `Detected causes: ${causeList.join(', ')}.`);
      }
    }
    
    // Note about skipCmp filtering
    if (results.skipCmp) {
      summaryParts.push(language === 'cs'
        ? '💡 Shifty způsobené cookie consent bannerem byly odfiltrovány z výsledků.'
        : '💡 Cookie consent banner shifts were filtered from results.');
    }
    
    // CLS assessment
    const clsValue = results.cls || 0;
    let assessment;
    if (clsValue <= 0.1) {
      assessment = language === 'cs' 
        ? 'CLS je v pořádku (pod 0.1).' 
        : 'CLS is good (under 0.1).';
    } else if (clsValue <= 0.25) {
      assessment = language === 'cs'
        ? 'CLS potřebuje zlepšení (0.1 - 0.25).'
        : 'CLS needs improvement (0.1 - 0.25).';
    } else {
      assessment = language === 'cs'
        ? 'CLS je špatné a vyžaduje okamžitou pozornost (nad 0.25).'
        : 'CLS is poor and needs immediate attention (above 0.25).';
    }
    summaryParts.push(assessment);
    
    // Phase breakdown if available
    const phases = results.phases;
    const hasPhases = phases && (phases.initial > 0 || phases.scroll > 0 || phases.postScroll > 0);
    
    return (
      <div className="p-4 rounded-lg bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border border-indigo-500/30 mb-3">
        <div className="text-xs uppercase tracking-wider text-indigo-400 mb-2 flex items-center gap-1">
          <Icons.FileText size={12} /> {language === 'cs' ? 'Shrnutí' : 'Summary'}
        </div>
        <div className="text-sm text-slate-200 space-y-1">
          {summaryParts.map((part, i) => (
            <p key={i}>{part}</p>
          ))}
        </div>
        
        {hasPhases && (
          <div className="mt-3 pt-3 border-t border-indigo-500/20">
            <div className="text-xs text-indigo-300 mb-2">
              {language === 'cs' ? 'Kdy vznikly shifty:' : 'When shifts occurred:'}
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className={`p-2 rounded text-center ${phases.initial > 0.05 ? 'bg-amber-900/30 border border-amber-500/30' : 'bg-slate-800/50 border border-slate-700'}`}>
                <div className={`font-bold ${phases.initial > 0.05 ? 'text-amber-400' : 'text-green-400'}`}>
                  {phases.initial?.toFixed(3) || '0.000'}
                </div>
                <div className="text-slate-400">{language === 'cs' ? 'Načtení' : 'Initial'}</div>
              </div>
              <div className={`p-2 rounded text-center ${phases.scroll > 0.05 ? 'bg-amber-900/30 border border-amber-500/30' : 'bg-slate-800/50 border border-slate-700'}`}>
                <div className={`font-bold ${phases.scroll > 0.05 ? 'text-amber-400' : 'text-green-400'}`}>
                  {phases.scroll?.toFixed(3) || '0.000'}
                </div>
                <div className="text-slate-400">{language === 'cs' ? 'Scroll' : 'Scroll'}</div>
              </div>
              <div className={`p-2 rounded text-center ${phases.postScroll > 0.05 ? 'bg-amber-900/30 border border-amber-500/30' : 'bg-slate-800/50 border border-slate-700'}`}>
                <div className={`font-bold ${phases.postScroll > 0.05 ? 'text-amber-400' : 'text-green-400'}`}>
                  {phases.postScroll?.toFixed(3) || '0.000'}
                </div>
                <div className="text-slate-400">{language === 'cs' ? 'Async' : 'Async'}</div>
              </div>
            </div>
            {(phases.scroll > 0.01 || phases.postScroll > 0.01) && (
              <p className="text-xs text-slate-400 mt-2">
                {language === 'cs' 
                  ? '⚠️ Posuny při scrollu nebo asynchronním načítání mohou vysvětlit vyšší hodnoty v reálných datech.'
                  : '⚠️ Scroll or async shifts may explain higher values in real user data.'}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCLSDiagnostics = () => {
    const cls = diagnostics?.cls;
    if (!cls) return null;

    const hasElements = cls.elements?.length > 0;
    const hasIssues = cls.issues?.length > 0;
    const hasCauses = cls.causes?.length > 0;

    const causeIcons = {
      'unsized-images': { icon: Icons.Image, color: 'text-red-400', bg: 'from-red-900/30 to-orange-900/30', border: 'border-red-500/30' },
      'unsized-image': { icon: Icons.Image, color: 'text-red-400', bg: 'from-red-900/30 to-orange-900/30', border: 'border-red-500/30' },
      'ads': { icon: Icons.DollarSign, color: 'text-amber-400', bg: 'from-amber-900/30 to-yellow-900/30', border: 'border-amber-500/30' },
      'ad-injection': { icon: Icons.DollarSign, color: 'text-amber-400', bg: 'from-amber-900/30 to-yellow-900/30', border: 'border-amber-500/30' },
      'fonts': { icon: Icons.Type, color: 'text-blue-400', bg: 'from-blue-900/30 to-cyan-900/30', border: 'border-blue-500/30' },
      'font-swap': { icon: Icons.Type, color: 'text-blue-400', bg: 'from-blue-900/30 to-cyan-900/30', border: 'border-blue-500/30' },
      'animations': { icon: Icons.Zap, color: 'text-purple-400', bg: 'from-purple-900/30 to-pink-900/30', border: 'border-purple-500/30' },
      'animation': { icon: Icons.Zap, color: 'text-purple-400', bg: 'from-purple-900/30 to-pink-900/30', border: 'border-purple-500/30' },
      'embeds': { icon: Icons.Layout, color: 'text-cyan-400', bg: 'from-cyan-900/30 to-blue-900/30', border: 'border-cyan-500/30' },
      'iframe-embed': { icon: Icons.Layout, color: 'text-cyan-400', bg: 'from-cyan-900/30 to-blue-900/30', border: 'border-cyan-500/30' },
      'video-embed': { icon: Icons.Layout, color: 'text-cyan-400', bg: 'from-cyan-900/30 to-blue-900/30', border: 'border-cyan-500/30' },
      'lazy-content': { icon: Icons.Clock, color: 'text-slate-400', bg: 'from-slate-800/50 to-slate-700/30', border: 'border-slate-600' },
      'dynamic-content': { icon: Icons.Clock, color: 'text-orange-400', bg: 'from-orange-900/30 to-amber-900/30', border: 'border-orange-500/30' }
    };

    const causeLabels = {
      'unsized-images': { cs: 'Obrázky bez rozměrů', en: 'Unsized Images' },
      'unsized-image': { cs: 'Obrázky bez rozměrů', en: 'Unsized Images' },
      'ads': { cs: 'Reklamní skripty', en: 'Ad Scripts' },
      'ad-injection': { cs: 'Reklamní skripty', en: 'Ad Scripts' },
      'fonts': { cs: 'Webfonty (FOUT)', en: 'Web Fonts (FOUT)' },
      'font-swap': { cs: 'Webfonty (FOUT)', en: 'Web Fonts (FOUT)' },
      'animations': { cs: 'Animace', en: 'Animations' },
      'animation': { cs: 'Animace', en: 'Animations' },
      'embeds': { cs: 'Vložené prvky', en: 'Embedded Content' },
      'iframe-embed': { cs: 'Vložené prvky (iframe)', en: 'Embedded Content (iframe)' },
      'video-embed': { cs: 'Vložené prvky (video)', en: 'Embedded Content (video)' },
      'lazy-content': { cs: 'Lazy-loaded obsah', en: 'Lazy-loaded Content' },
      'dynamic-content': { cs: 'Dynamický obsah', en: 'Dynamic Content' }
    };

    const causeDescriptions = {
      'unsized-images': {
        cs: 'Obrázky bez atributů width/height způsobují posun layoutu při načtení.',
        en: 'Images without width/height attributes cause layout shift when loaded.'
      },
      'unsized-image': {
        cs: 'Obrázky bez atributů width/height způsobují posun layoutu při načtení.',
        en: 'Images without width/height attributes cause layout shift when loaded.'
      },
      'ads': {
        cs: 'Reklamní skripty dynamicky vkládají obsah a posouvají layout.',
        en: 'Ad scripts dynamically inject content and shift the layout.'
      },
      'ad-injection': {
        cs: 'Reklamní skripty dynamicky vkládají obsah a posouvají layout.',
        en: 'Ad scripts dynamically inject content and shift the layout.'
      },
      'fonts': {
        cs: 'Webfonty bez font-display:swap způsobují blikání textu (FOUT/FOIT).',
        en: 'Web fonts without font-display:swap cause text flashing (FOUT/FOIT).'
      },
      'font-swap': {
        cs: 'Webfonty bez font-display:swap způsobují blikání textu (FOUT/FOIT).',
        en: 'Web fonts without font-display:swap cause text flashing (FOUT/FOIT).'
      },
      'animations': {
        cs: 'Animace měnící layout properties (width, height, top, left) místo transform.',
        en: 'Animations changing layout properties instead of transform.'
      },
      'animation': {
        cs: 'Animace měnící layout properties (width, height, top, left) místo transform.',
        en: 'Animations changing layout properties instead of transform.'
      },
      'embeds': {
        cs: 'Vložené prvky (iframe, video) bez rezervovaného místa.',
        en: 'Embedded elements (iframe, video) without reserved space.'
      },
      'iframe-embed': {
        cs: 'Iframe bez rezervovaného místa způsobuje posun layoutu.',
        en: 'Iframe without reserved space causes layout shift.'
      },
      'video-embed': {
        cs: 'Video bez rezervovaného místa způsobuje posun layoutu.',
        en: 'Video without reserved space causes layout shift.'
      },
      'lazy-content': {
        cs: 'Lazy-loaded obsah nad foldem může způsobit pozdní posuny.',
        en: 'Lazy-loaded content above fold may cause late shifts.'
      },
      'dynamic-content': {
        cs: 'Dynamicky vkládaný obsah (widgety, skripty) způsobuje posuny layoutu.',
        en: 'Dynamically injected content (widgets, scripts) causes layout shifts.'
      }
    };

    const causeRecommendations = {
      'unsized-images': {
        cs: ['Přidejte width a height atributy ke všem obrázkům', 'Použijte aspect-ratio CSS property'],
        en: ['Add width and height attributes to all images', 'Use aspect-ratio CSS property']
      },
      'unsized-image': {
        cs: ['Přidejte width a height atributy ke všem obrázkům', 'Použijte aspect-ratio CSS property'],
        en: ['Add width and height attributes to all images', 'Use aspect-ratio CSS property']
      },
      'ads': {
        cs: ['Rezervujte místo pomocí min-height', 'Použijte aspect-ratio pro reklamní kontejnery'],
        en: ['Reserve space using min-height', 'Use aspect-ratio for ad containers']
      },
      'ad-injection': {
        cs: ['Rezervujte místo pomocí min-height', 'Použijte aspect-ratio pro reklamní kontejnery'],
        en: ['Reserve space using min-height', 'Use aspect-ratio for ad containers']
      },
      'fonts': {
        cs: ['Přidejte font-display: swap do @font-face', 'Preloadujte kritické fonty'],
        en: ['Add font-display: swap to @font-face', 'Preload critical fonts']
      },
      'font-swap': {
        cs: ['Přidejte font-display: swap do @font-face', 'Preloadujte kritické fonty'],
        en: ['Add font-display: swap to @font-face', 'Preload critical fonts']
      },
      'animations': {
        cs: ['Použijte transform místo top/left/width/height', 'Animujte pouze opacity a transform'],
        en: ['Use transform instead of top/left/width/height', 'Only animate opacity and transform']
      },
      'animation': {
        cs: ['Použijte transform místo top/left/width/height', 'Animujte pouze opacity a transform'],
        en: ['Use transform instead of top/left/width/height', 'Only animate opacity and transform']
      },
      'embeds': {
        cs: ['Rezervujte místo pomocí aspect-ratio kontejneru', 'Lazy-loadujte embedy pod foldem'],
        en: ['Reserve space using aspect-ratio container', 'Lazy-load embeds below fold']
      },
      'iframe-embed': {
        cs: ['Rezervujte místo pomocí aspect-ratio kontejneru', 'Lazy-loadujte iframe pod foldem'],
        en: ['Reserve space using aspect-ratio container', 'Lazy-load iframe below fold']
      },
      'video-embed': {
        cs: ['Rezervujte místo pomocí aspect-ratio kontejneru', 'Použijte poster atribut pro video'],
        en: ['Reserve space using aspect-ratio container', 'Use poster attribute for video']
      },
      'lazy-content': {
        cs: ['Rezervujte místo pro lazy obsah', 'Použijte skeleton loadery'],
        en: ['Reserve space for lazy content', 'Use skeleton loaders']
      },
      'dynamic-content': {
        cs: ['Rezervujte místo pro dynamický obsah', 'Použijte skeleton loadery'],
        en: ['Reserve space for dynamic content', 'Use skeleton loaders']
      }
    };
    
    const causeExamples = {
      'unsized-images': {
        bad: '<img src="foto.jpg" alt="Foto">',
        good: '<img src="foto.jpg" alt="Foto" width="800" height="600">'
      },
      'unsized-image': {
        bad: '<img src="foto.jpg" alt="Foto">',
        good: '<img src="foto.jpg" alt="Foto" width="800" height="600">'
      },
      'animations': {
        bad: '@keyframes move { to { left: 200px; } }',
        good: '@keyframes move { to { transform: translateX(200px); } }'
      },
      'animation': {
        bad: '@keyframes move { to { left: 200px; } }',
        good: '@keyframes move { to { transform: translateX(200px); } }'
      },
      'embeds': {
        bad: '<iframe src="..."></iframe>',
        good: '<div style="aspect-ratio: 16/9;"><iframe src="..." style="width:100%; height:100%;"></iframe></div>'
      },
      'iframe-embed': {
        bad: '<iframe src="..."></iframe>',
        good: '<div style="aspect-ratio: 16/9;"><iframe src="..." style="width:100%; height:100%;"></iframe></div>'
      },
      'video-embed': {
        bad: '<video src="..."></video>',
        good: '<video src="..." width="640" height="360" poster="preview.jpg"></video>'
      },
      'ads': {
        bad: '<div id="ad-container"></div>',
        good: '<div id="ad-container" style="min-height: 250px;"></div>'
      },
      'ad-injection': {
        bad: '<div id="ad-container"></div>',
        good: '<div id="ad-container" style="min-height: 250px;"></div>'
      },
      'fonts': {
        bad: '@font-face { font-family: "MyFont"; src: url(...); }',
        good: '@font-face { font-family: "MyFont"; src: url(...); font-display: swap; }'
      },
      'font-swap': {
        bad: '@font-face { font-family: "MyFont"; src: url(...); }',
        good: '@font-face { font-family: "MyFont"; src: url(...); font-display: swap; }'
      },
      'dynamic-content': {
        bad: '<div id="content"></div>',
        good: '<div id="content" style="min-height: 200px;"></div>'
      },
      'lazy-content': {
        bad: '<div class="lazy-section"></div>',
        good: '<div class="lazy-section skeleton" style="min-height: 300px;"></div>'
      }
    };

    return (
      <div className="space-y-4">
        {hasElements && (
          <div className="p-4 rounded-lg bg-gradient-to-r from-amber-900/30 to-orange-900/30 border border-amber-500/30">
            <div className="text-xs uppercase tracking-wider text-amber-400 mb-2 flex items-center gap-1">
              <Icons.Move size={12} /> {t('metricsDisplay.diagnostics.shiftingElements')}
            </div>
            <div className="space-y-2">
              {cls.elements.slice(0, 5).map((el, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-slate-900/50 rounded">
                  <code className="text-xs text-white truncate max-w-xs">
                    {el.node || el.selector || 'Unknown element'}
                  </code>
                  <span className="text-xs text-amber-400 ml-2">
                    shift: {el.score?.toFixed(4) || 'N/A'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {hasCauses && (
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wider text-orange-400 flex items-center gap-1">
              <Icons.Target size={12} /> {language === 'cs' ? 'Detekované příčiny' : 'Detected Causes'}
            </div>
            {cls.causes.map((cause, i) => {
              const style = causeIcons[cause.type] || causeIcons['lazy-content'];
              const IconComponent = style.icon || Icons.AlertTriangle;
              return (
                <div key={i} className={`p-4 rounded-lg bg-gradient-to-r ${style.bg} border ${style.border}`}>
                  <div className={`text-sm font-medium ${style.color} mb-1 flex items-center gap-2`}>
                    <IconComponent size={14} />
                    {causeLabels[cause.type]?.[language] || cause.type}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      cause.severity === 'high' ? 'bg-red-500/30 text-red-300' : 
                      cause.severity === 'medium' ? 'bg-amber-500/30 text-amber-300' : 
                      'bg-slate-500/30 text-slate-300'
                    }`}>
                      {cause.severity === 'high' ? (language === 'cs' ? 'vysoká' : 'high') :
                       cause.severity === 'medium' ? (language === 'cs' ? 'střední' : 'medium') :
                       (language === 'cs' ? 'nízká' : 'low')}
                    </span>
                    {cause.count > 1 && (
                      <span className="text-xs text-slate-400" title={language === 'cs' ? `Způsobilo ${cause.count} posunutí obsahu` : `Caused ${cause.count} layout shifts`}>
                        ({cause.count}× {language === 'cs' ? 'posun' : 'shift'})
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-300 mb-2">
                    {causeDescriptions[cause.type]?.[language]}
                    {cause.count > 1 && (
                      <span className="text-slate-400">
                        {' '}({language === 'cs' 
                          ? `Tento typ problému způsobil ${cause.count} posunutí obsahu během načítání stránky.`
                          : `This issue caused ${cause.count} content shifts during page load.`})
                      </span>
                    )}
                  </p>
                  {cause.entities?.length > 0 && (
                    <div className="text-xs text-slate-400 mb-2">
                      {cause.entities.join(', ')}
                    </div>
                  )}
                  {cause.items?.length > 0 && (
                    <div className="mb-2 space-y-1">
                      {cause.items.map((url, j) => (
                        <code key={j} className="block text-xs text-slate-400 bg-slate-900/50 px-2 py-1 rounded break-all" title={typeof url === 'string' ? url : ''}>
                          {typeof url === 'string' ? url : url}
                        </code>
                      ))}
                    </div>
                  )}
                  <div className="text-xs text-slate-400">
                    <span className="font-medium">{language === 'cs' ? 'Doporučení:' : 'Fix:'}</span>
                    <ul className="list-disc list-inside mt-1 space-y-0.5 ml-2">
                      {causeRecommendations[cause.type]?.[language]?.slice(0, 2).map((rec, j) => (
                        <li key={j}>{rec}</li>
                      ))}
                    </ul>
                  </div>
                  {causeExamples[cause.type] && (
                    <div className="mt-3 space-y-2">
                      <div className="text-xs font-medium text-slate-400">
                        {language === 'cs' ? 'Jak opravit:' : 'How to fix:'}
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        <div className="p-2 rounded bg-red-900/20 border border-red-500/20">
                          <div className="text-[10px] text-red-400 mb-1 flex items-center gap-1">
                            <Icons.X size={10} /> {language === 'cs' ? 'Aktuální stav (problém)' : 'Current (problem)'}
                          </div>
                          <code className="text-[11px] text-red-300 break-all">
                            {cause.sampleHTML || causeExamples[cause.type].bad}
                          </code>
                        </div>
                        <div className="p-2 rounded bg-green-900/20 border border-green-500/20">
                          <div className="text-[10px] text-green-400 mb-1 flex items-center gap-1">
                            <Icons.Check size={10} /> {language === 'cs' ? 'Po opravě' : 'After fix'}
                          </div>
                          <code className="text-[11px] text-green-300 break-all">
                            {cause.fixedHTML || causeExamples[cause.type].good}
                          </code>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!hasElements && !hasCauses && (
          <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-600">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
              <Icons.Info size={12} /> {language === 'cs' ? 'Informace' : 'Info'}
            </div>
            <p className="text-sm text-slate-300">
              {language === 'cs' 
                ? 'Konkrétní příčiny layout shiftu nebyly detekovány. CLS může být způsoben: dynamicky načítaným obsahem, obrázky bez rozměrů, web fonty (FOUT), nebo vkládanými prvky (iframe, embed).'
                : 'Specific layout shift causes were not detected. CLS may be caused by: dynamically loaded content, images without dimensions, web fonts (FOUT), or embedded elements (iframe, embed).'}
            </p>
          </div>
        )}
        
        {hasIssues && (
          <div>
            <div className="text-xs uppercase tracking-wider text-red-400 mb-2 flex items-center gap-1">
              <Icons.AlertTriangle size={12} /> {t('metricsDisplay.diagnostics.issuesFound')}
            </div>
            <div className="space-y-2">
              {cls.issues.map(renderIssue)}
            </div>
          </div>
        )}

        {user && (
          <div className="mt-4 pt-4 border-t border-slate-700">
            {!deepCLSResults ? (
              <div className="space-y-3">
                {/* CMP Toggle */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <div className="flex-1">
                    <div className="text-sm text-white font-medium">
                      {language === 'cs' ? 'Přeskočit cookie banner' : 'Skip cookie banner'}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {language === 'cs' 
                        ? 'Pokusí se automaticky akceptovat běžné CMP (Didomi, Cookiebot, OneTrust)'
                        : 'Attempts to auto-accept common CMPs (Didomi, Cookiebot, OneTrust)'}
                    </div>
                  </div>
                  <button
                    onClick={() => setSkipCmp(!skipCmp)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${skipCmp ? 'bg-purple-600' : 'bg-slate-600'}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${skipCmp ? 'left-7' : 'left-1'}`} />
                  </button>
                </div>

                {/* Capture visuals toggle - Premium feature */}
                <div className="flex items-center justify-between p-2 rounded-lg bg-slate-800/50 border border-slate-700">
                  <div className="flex-1 mr-3">
                    <div className="flex items-center gap-2">
                      <Icons.Camera size={14} className="text-purple-400" />
                      <span className="text-sm text-white">
                        {language === 'cs' ? 'Vizuální náhled' : 'Visual Preview'}
                      </span>
                      {isPremium ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/30 text-purple-300">Premium</span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-500/30 text-slate-400">
                          <Icons.Lock size={8} className="inline mr-0.5" />
                          Premium
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {language === 'cs' 
                        ? 'Zachytí screenshoty a HTML kód problémových elementů'
                        : 'Captures screenshots and HTML code of problematic elements'}
                    </div>
                  </div>
                  <button
                    onClick={() => isPremium && setCaptureVisuals(!captureVisuals)}
                    disabled={!isPremium}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      !isPremium ? 'bg-slate-700 cursor-not-allowed opacity-50' :
                      captureVisuals ? 'bg-purple-600' : 'bg-slate-600'
                    }`}
                  >
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${captureVisuals && isPremium ? 'left-7' : 'left-1'}`} />
                  </button>
                </div>

                <button
                  onClick={runDeepCLSDiagnostics}
                  disabled={deepCLSLoading}
                  className="w-full py-3 px-4 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {deepCLSLoading ? (
                    <>
                      <Icons.Loader size={16} className="animate-spin" />
                      {language === 'cs' ? 'Analyzuji layout shifty...' : 'Analyzing layout shifts...'}
                    </>
                  ) : (
                    <>
                      <Icons.Search size={16} />
                      {language === 'cs' ? 'Spustit hloubkovou CLS analýzu' : 'Run Deep CLS Analysis'}
                      {skipCmp && <span className="text-xs opacity-75">({language === 'cs' ? 'bez CMP' : 'no CMP'})</span>}
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-wider text-purple-400 flex items-center gap-1">
                    <Icons.Zap size={12} /> {language === 'cs' ? 'Hloubková analýza (Lab)' : 'Deep Analysis (Lab)'}
                    {deepCLSResults.skipCmp && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 ml-1">
                        {language === 'cs' ? 'bez CMP' : 'no CMP'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-lg font-bold ${deepCLSResults.cls <= 0.1 ? 'text-green-400' : deepCLSResults.cls <= 0.25 ? 'text-amber-400' : 'text-red-400'}`}>
                      CLS: {deepCLSResults.cls?.toFixed(3)}
                    </span>
                    <button 
                      onClick={resetDeepCLSResults}
                      className="text-xs text-slate-400 hover:text-white transition-colors"
                      title={language === 'cs' ? 'Spustit znovu' : 'Run again'}
                    >
                      <Icons.RefreshCw size={14} />
                    </button>
                  </div>
                </div>
                
                {/* Explain difference between lab and real user data */}
                <div className="text-xs text-slate-400 bg-slate-800/50 p-2 rounded border border-slate-700 space-y-1">
                  <div>
                    <span className="text-purple-400 font-medium">{language === 'cs' ? 'Tento test' : 'This test'}</span> {language === 'cs' 
                      ? '= jednorázová simulace této konkrétní stránky.'
                      : '= one-time simulation of this specific page.'}
                  </div>
                  <div>
                    <span className="text-cyan-400 font-medium">{language === 'cs' ? 'Reálná data' : 'Real data'}</span> {language === 'cs' 
                      ? '= průměr od skutečných návštěvníků za posledních 28 dní na celém webu (všechny stránky), ne jen tato.'
                      : '= average from real visitors over 28 days across entire site (all pages), not just this one.'}
                  </div>
                </div>

                {/* User-friendly summary */}
                {renderDeepCLSSummary(deepCLSResults)}
                <DiagnosticEvidenceView result={deepCLSResults} metric="CLS" language={language} />
                
                {deepCLSResults.elements?.length > 0 && (
                  <div className="p-3 rounded-lg bg-purple-900/20 border border-purple-500/30">
                    <div className="text-xs text-purple-400 mb-2 font-medium">
                      {language === 'cs' ? 'Elementy způsobující shift:' : 'Elements causing shift:'}
                    </div>
                    <div className="space-y-2">
                      {deepCLSResults.elements.slice(0, 5).map((el, i) => {
                        const causeLabels = {
                          'consent-banner': language === 'cs' ? 'cookie banner' : 'cookie banner',
                          'font-swap': language === 'cs' ? 'font swap' : 'font swap',
                          'ad-injection': language === 'cs' ? 'reklama' : 'ad',
                          'unsized-image': language === 'cs' ? 'obrázek' : 'image',
                          'iframe-embed': 'iframe',
                          'video-embed': 'video',
                          'dynamic-content': language === 'cs' ? 'dynamický' : 'dynamic',
                          'animation': language === 'cs' ? 'animace' : 'animation',
                          'image-load': language === 'cs' ? 'obrázek' : 'image'
                        };
                        const displayCause = el.friendlyName || causeLabels[el.cause] || el.cause;
                        const timing = el.timing ? `@ ${(el.timing / 1000).toFixed(1)}s` : '';
                        
                        return (
                          <div key={i} className="p-2 bg-slate-900/50 rounded">
                            <div className="flex items-center justify-between mb-1">
                              <code className="text-xs text-white break-all" title={el.selector}>{el.selector}</code>
                              <span className="text-xs text-amber-400 whitespace-nowrap ml-2">shift: {el.totalShift?.toFixed(4)}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                el.cause === 'unsized-image' || el.cause === 'ad-injection' ? 'bg-red-500/30 text-red-300' :
                                el.cause === 'consent-banner' ? 'bg-blue-500/30 text-blue-300' :
                                el.cause === 'font-swap' || el.cause === 'iframe-embed' ? 'bg-amber-500/30 text-amber-300' :
                                'bg-slate-500/30 text-slate-300'
                              }`}>
                                {displayCause}
                              </span>
                              <span className="text-xs text-slate-500">{el.tagName}</span>
                              {timing && <span className="text-xs text-slate-500">{timing}</span>}
                            </div>
                            <ElementVisualPreview 
                              element={el} 
                              url={url} 
                              isPremium={isPremium}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {deepCLSResults.recommendations?.length > 0 && (
                  <div className="space-y-2">
                    {deepCLSResults.recommendations.map((rec, i) => (
                      <div key={i} className={`p-3 rounded-lg border ${
                        rec.severity === 'high' ? 'bg-red-900/20 border-red-500/30' :
                        rec.severity === 'medium' ? 'bg-amber-900/20 border-amber-500/30' :
                        'bg-slate-800/50 border-slate-600'
                      }`}>
                        <div className={`text-sm font-medium mb-1 ${
                          rec.severity === 'high' ? 'text-red-400' :
                          rec.severity === 'medium' ? 'text-amber-400' : 'text-slate-300'
                        }`}>
                          {rec.title?.[language]}
                        </div>
                        <p className="text-xs text-slate-400 mb-2">{rec.description?.[language]}</p>
                        <ul className="text-xs text-slate-500 list-disc list-inside space-y-0.5">
                          {rec.fix?.[language]?.slice(0, 2).map((fix, j) => (
                            <li key={j}>{fix}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Multi-page CLS section */}
                <div className="mt-4 pt-4 border-t border-slate-700">
                  {!multiPageResults ? (
                    <button
                      onClick={runMultiPageCLS}
                      disabled={multiPageLoading}
                      className="w-full py-2 px-3 rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {multiPageLoading ? (
                        <>
                          <Icons.Loader size={14} className="animate-spin" />
                          {multiPageProgress ? (
                            <span>
                              {language === 'cs' 
                                ? `Analyzuji ${multiPageProgress.current}/${multiPageProgress.total} stránek...` 
                                : `Analyzing ${multiPageProgress.current}/${multiPageProgress.total} pages...`}
                              <span className="text-xs opacity-70 ml-1">
                                (~{Math.round((multiPageProgress.total - multiPageProgress.current) * 0.5)} min)
                              </span>
                            </span>
                          ) : (
                            language === 'cs' ? 'Připravuji analýzu...' : 'Preparing analysis...'
                          )}
                        </>
                      ) : (
                        <>
                          <Icons.Globe size={14} />
                          {language === 'cs' ? 'Analyzovat CLS na více stránkách' : 'Analyze CLS on multiple pages'}
                        </>
                      )}
                    </button>
                  ) : (
                    renderMultiPageCLSResults()
                  )}
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    {language === 'cs' 
                      ? 'Zjistí, které stránky webu mají nejvyšší CLS a mohou zhoršovat celkové hodnocení.'
                      : 'Identifies which pages have highest CLS and may be degrading overall score.'}
                  </p>
                </div>
                
                <ActionPlan 
                  deepCLSResults={deepCLSResults} 
                  multiPageResults={multiPageResults}
                  url={url}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const runDeepCLSDiagnostics = async () => {
    if (!url || !user) return;
    
    if (!hasDeepAccess) {
      setShowUpgradeModal(true);
      return;
    }
    
    setDeepCLSLoading(true);
    try {
      const response = await fetch('/api/lab-audits/cls-diagnostics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url, skipCmp, captureVisuals: captureVisuals && isPremium })
      });
      
      const data = await response.json();
      if (data.upgrade_required) {
        setShowUpgradeModal(true);
        return;
      }
      if (data.success && data.data) {
        setDeepCLSResults(data.data);
      }
    } catch (err) {
      console.error('Deep CLS diagnostics error:', err);
    } finally {
      setDeepCLSLoading(false);
    }
  };

  // Reset results to allow re-testing with different settings
  const resetDeepCLSResults = () => {
    setDeepCLSResults(null);
  };

  const runMultiPageCLS = async () => {
    if (!url || !user) return;
    
    if (!hasDeepAccess) {
      setShowUpgradeModal(true);
      return;
    }
    
    setMultiPageLoading(true);
    try {
      const response = await fetch('/api/lab-audits/multi-page-cls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url, maxPages: 10, skipCmp: true })
      });
      
      const data = await response.json();
      if (data.upgrade_required) {
        setShowUpgradeModal(true);
        setMultiPageLoading(false);
        return;
      }
      
      // Handle async job response
      if (data.success && data.async && data.jobId) {
        // Poll for job completion
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await fetch(`/api/lab-audits/multi-page-cls/status/${data.jobId}`, {
              credentials: 'include'
            });
            const statusData = await statusResponse.json();
            
            // Update progress if available
            if (statusData.progress) {
              setMultiPageProgress(statusData.progress);
            }
            
            if (statusData.status === 'completed' && statusData.data) {
              clearInterval(pollInterval);
              setMultiPageResults(statusData.data);
              setMultiPageLoading(false);
              setMultiPageProgress(null);
            } else if (statusData.status === 'failed') {
              clearInterval(pollInterval);
              console.error('Multi-page CLS job failed:', statusData.error);
              setMultiPageLoading(false);
              setMultiPageProgress(null);
            }
            // Continue polling if still pending/processing
          } catch (pollErr) {
            console.error('Multi-page CLS poll error:', pollErr);
          }
        }, 3000); // Poll every 3 seconds
        
        // Timeout after 10 minutes (matches backend timeout)
        setTimeout(() => {
          clearInterval(pollInterval);
          if (multiPageLoading) {
            setMultiPageLoading(false);
            setMultiPageProgress(null);
            console.error('Multi-page CLS timeout');
          }
        }, 600000);
        
        return;
      }
      
      // Handle direct (sync) response
      if (data.success && data.data) {
        setMultiPageResults(data.data);
        setMultiPageLoading(false);
      }
    } catch (err) {
      console.error('Multi-page CLS error:', err);
      setMultiPageLoading(false);
    }
  };

  const renderMultiPageCLSResults = () => {
    if (!multiPageResults) return null;
    
    const { summary, pages } = multiPageResults;
    const statusColor = (status) => {
      if (status === 'good') return 'text-green-400';
      if (status === 'needs-improvement') return 'text-amber-400';
      if (status === 'poor') return 'text-red-400';
      return 'text-slate-400';
    };
    
    const statusBg = (status) => {
      if (status === 'good') return 'bg-green-500/20';
      if (status === 'needs-improvement') return 'bg-amber-500/20';
      if (status === 'poor') return 'bg-red-500/20';
      return 'bg-slate-500/20';
    };

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-indigo-400 flex items-center gap-1">
            <Icons.Globe size={12} /> {language === 'cs' ? 'Multi-page CLS analýza' : 'Multi-Page CLS Analysis'}
          </div>
          <button 
            onClick={() => setMultiPageResults(null)}
            className="text-xs text-slate-400 hover:text-white transition-colors"
            title={language === 'cs' ? 'Zavřít' : 'Close'}
          >
            <Icons.X size={14} />
          </button>
        </div>
        
        <div className="grid grid-cols-3 gap-2">
          <div className="p-2 rounded bg-green-900/30 border border-green-500/30 text-center">
            <div className="text-lg font-bold text-green-400">{summary.goodPages}</div>
            <div className="text-xs text-green-300">{language === 'cs' ? 'Dobré' : 'Good'}</div>
          </div>
          <div className="p-2 rounded bg-amber-900/30 border border-amber-500/30 text-center">
            <div className="text-lg font-bold text-amber-400">{summary.needsImprovementPages}</div>
            <div className="text-xs text-amber-300">{language === 'cs' ? 'K zlepšení' : 'Needs work'}</div>
          </div>
          <div className="p-2 rounded bg-red-900/30 border border-red-500/30 text-center">
            <div className="text-lg font-bold text-red-400">{summary.poorPages}</div>
            <div className="text-xs text-red-300">{language === 'cs' ? 'Špatné' : 'Poor'}</div>
          </div>
        </div>
        <p className="text-xs text-slate-400">
          {language === 'cs'
            ? 'Pásma hodnotí agregát měřené stránky, nikoli každý jednotlivý běh; bez jeho hodnot nelze tvrdit, že všechny běhy byly dobré.'
            : 'Bands classify the measured-page aggregate, not every individual run; without its values not all runs can be claimed good.'}
        </p>
        
        <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
          <div className="text-xs text-slate-400 mb-2">
            {language === 'cs' ? 'Průměrné CLS:' : 'Average CLS:'} 
            <span className={`ml-1 font-bold ${summary.avgCLS <= 0.1 ? 'text-green-400' : summary.avgCLS <= 0.25 ? 'text-amber-400' : 'text-red-400'}`}>
              {summary.avgCLS?.toFixed(3)}
            </span>
          </div>
          
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {pages.sort((a, b) => (b.cls || 0) - (a.cls || 0)).map((page, i) => {
              const pathMatch = page.url.match(/https?:\/\/[^/]+(.*)/);
              const path = pathMatch ? pathMatch[1] || '/' : page.url;
              const isExpanded = expandedPageIndex === i;
              const hasCauses = page.causes && page.causes.length > 0;
              
              return (
                <div key={i} className="space-y-0">
                  <div 
                    className={`flex items-center justify-between p-2 rounded ${statusBg(page.status)} ${hasCauses ? 'cursor-pointer hover:opacity-80' : ''}`}
                    onClick={() => hasCauses && setExpandedPageIndex(isExpanded ? null : i)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        {hasCauses && (
                          <span className="text-slate-400 text-xs">
                            {isExpanded ? '▼' : '▶'}
                          </span>
                        )}
                        <code className="text-xs text-white break-all block" title={page.url}>
                          {path}
                        </code>
                      </div>
                      {page.topCause && !isExpanded && (
                        <span className="text-xs text-slate-400 ml-3">{page.topCause}</span>
                      )}
                    </div>
                    <div className={`text-sm font-mono font-bold ml-2 ${statusColor(page.status)}`}>
                      {page.cls !== null ? page.cls.toFixed(3) : 'Error'}
                    </div>
                  </div>
                  
                  {isExpanded && page.causes && page.causes.length > 0 && (
                    <div className="ml-4 pl-2 border-l-2 border-slate-600 py-1 space-y-1">
                      {page.causes.map((causeItem, ci) => (
                        <div key={ci} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-500">├─</span>
                            <span className="text-slate-300">{causeItem.cause}</span>
                          </div>
                          <span className="text-slate-400 text-xs">
                            {causeItem.count}×
                          </span>
                        </div>
                      ))}
                      {page.topElement && (
                        <div className="flex items-start gap-2 text-xs mt-1 pt-1 border-t border-slate-700">
                          <span className="text-slate-500">└─</span>
                          <code className="text-purple-400 text-xs break-all">
                            {page.topElement}
                          </code>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {summary.worstPage && summary.maxCLS > 0.1 && (
          <div className="p-3 rounded-lg bg-red-900/20 border border-red-500/30">
            <div className="text-xs text-red-400 mb-1">
              {language === 'cs' ? 'Nejhorší stránka:' : 'Worst page:'}
            </div>
            <code className="text-xs text-white break-all">{summary.worstPage}</code>
            <div className="text-xs text-slate-400 mt-1">
              CLS: <span className="text-red-400 font-bold">{summary.maxCLS?.toFixed(3)}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderINPDiagnostics = () => {
    const inp = diagnostics?.inp;
    if (!inp) return null;

    return (
      <div className="space-y-4">
        {inp.tbt && (
          <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 flex items-center justify-between">
            <span className="text-sm text-slate-300">{t('metricsDisplay.diagnostics.totalBlockingTime')} · {diagnostics?.labRunSpread?.conditions?.networkProfile === 'controlled-lighthouse'
              ? (language === 'cs' ? 'lokální Lighthouse' : 'local Lighthouse') : 'Lighthouse / PSI'}</span>
            <span className={`text-lg font-bold ${inp.tbt > 300 ? 'text-red-400' : inp.tbt > 150 ? 'text-amber-400' : 'text-green-400'}`}>
              {Math.round(inp.tbt)}ms
            </span>
          </div>
        )}
        
        {inp.longTasks?.length > 0 && (
          <div className="p-4 rounded-lg bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30">
            <div className="text-xs uppercase tracking-wider text-purple-400 mb-2 flex items-center gap-1">
              <Icons.Clock size={12} /> {t('metricsDisplay.diagnostics.longTasks')}
            </div>
            <div className="space-y-2">
              {inp.longTasks.map((task, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-slate-900/50 rounded">
                  <code className="text-xs text-white break-all">
                    {task.url || 'Main document'}
                  </code>
                  <span className="text-xs text-purple-400 ml-2">
                    {Math.round(task.duration)}ms
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {inp.issues?.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-red-400 mb-2 flex items-center gap-1">
              <Icons.AlertTriangle size={12} /> {t('metricsDisplay.diagnostics.issuesFound')}
            </div>
            <div className="space-y-2">
              {inp.issues.map(renderIssue)}
            </div>
          </div>
        )}

        {user && (
          <div className="mt-4 pt-4 border-t border-slate-700">
            <INPDiagnosticsPanel url={url} user={user} device={selectedDevice || strategy} onResultsChange={(data) => {
              if (data.deepINP) setDeepINPResults(data.deepINP);
              if (data.inpCompare) setInpCompareResults(data.inpCompare);
              if (data.multiPageINP) setMultiPageINPResults(data.multiPageINP);
            }} />
            <DiagnosticEvidenceView result={deepINPResults} metric="INP" language={language} />
          </div>
        )}
      </div>
    );
  };

  const renderTTFBDiagnostics = () => {
    const ttfb = diagnostics?.ttfb;
    if (!ttfb) return null;

    return (
      <div className="space-y-4">
        {ttfb.issues?.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-red-400 mb-2 flex items-center gap-1">
              <Icons.AlertTriangle size={12} /> {t('metricsDisplay.diagnostics.issuesFound')}
            </div>
            <div className="space-y-2">
              {ttfb.issues.map(renderIssue)}
            </div>
          </div>
        )}

        {user && (
          <div className="mt-4 pt-4 border-t border-slate-700">
            <TTFBDiagnosticsPanel url={url} user={user} onResultsChange={setDeepTTFBResults} />
          </div>
        )}
      </div>
    );
  };

  const renderRTTDiagnostics = () => {
    return (
      <div className="space-y-4">
        <div className="p-4 rounded-lg bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border border-cyan-500/30">
          <div className="text-xs uppercase tracking-wider text-cyan-400 mb-2 flex items-center gap-1">
            <Icons.Zap size={12} /> {language === 'cs' ? 'Síťová latence (RTT)' : 'Network Latency (RTT)'}
          </div>
          <p className="text-sm text-slate-300">
            {language === 'cs' 
              ? 'RTT měří čas potřebný pro navázání spojení se serverem včetně DNS, TCP a TLS handshake.'
              : 'RTT measures the time needed to establish connection including DNS, TCP and TLS handshake.'}
          </p>
        </div>
        {user && (
          <div className="mt-4 pt-4 border-t border-slate-700">
            <RTTDiagnosticsPanel url={url} user={user} />
          </div>
        )}
      </div>
    );
  };

  const renderFCPDiagnostics = () => {
    return (
      <div className="space-y-4">
        <div className="p-4 rounded-lg bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/30">
          <div className="text-xs uppercase tracking-wider text-green-400 mb-2 flex items-center gap-1">
            <Icons.Image size={12} /> {language === 'cs' ? 'První vykreslení obsahu (FCP)' : 'First Contentful Paint (FCP)'}
          </div>
          <p className="text-sm text-slate-300">
            {language === 'cs' 
              ? 'FCP měří čas do prvního vykreslení textu nebo obrázku. Ovlivňuje ho TTFB, render-blocking zdroje a velikost kritického CSS.'
              : 'FCP measures time until first text or image is painted. Affected by TTFB, render-blocking resources and critical CSS size.'}
          </p>
        </div>
        <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
          <h5 className="text-sm font-medium text-white mb-2">
            {language === 'cs' ? 'Jak zlepšit FCP:' : 'How to improve FCP:'}
          </h5>
          <ul className="text-xs text-slate-400 space-y-1">
            <li>• {language === 'cs' ? 'Optimalizujte TTFB (rychlejší server)' : 'Optimize TTFB (faster server)'}</li>
            <li>• {language === 'cs' ? 'Odstraňte render-blocking JavaScript a CSS' : 'Remove render-blocking JavaScript and CSS'}</li>
            <li>• {language === 'cs' ? 'Inline kritický CSS' : 'Inline critical CSS'}</li>
            <li>• {language === 'cs' ? 'Použijte preload pro důležité fonty' : 'Use preload for important fonts'}</li>
          </ul>
        </div>
      </div>
    );
  };

  const renderRenderBlockingResources = () => {
    const rb = diagnostics?.renderBlocking;
    if (!rb?.items?.length) return null;

    return (
      <div className="p-4 rounded-lg bg-gradient-to-r from-red-900/30 to-orange-900/30 border border-red-500/30">
        <div className="text-xs uppercase tracking-wider text-red-400 mb-2 flex items-center gap-1">
          <Icons.StopCircle size={12} /> {t('metricsDisplay.diagnostics.renderBlocking')}
          {rb.totalWastedMs > 0 && (
            <span className="ml-auto text-amber-400">-{Math.round(rb.totalWastedMs)}ms</span>
          )}
        </div>
        <div className="space-y-2">
          {rb.items.slice(0, 5).map((item, i) => (
            <div key={i} className="flex items-center justify-between p-2 bg-slate-900/50 rounded text-xs">
              <span className="text-white break-all text-[10px]">{item.url}</span>
              <div className="flex items-center gap-2 ml-2">
                <span className="text-slate-400">{formatBytes(item.totalBytes)}</span>
                <span className="text-amber-400">-{Math.round(item.wastedMs)}ms</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderUnusedResources = () => {
    const unused = diagnostics?.unusedResources;
    if (!unused) return null;
    
    const hasUnused = unused.javascript?.items?.length > 0 || unused.css?.items?.length > 0;
    if (!hasUnused) return null;

    return (
      <div className="p-4 rounded-lg bg-gradient-to-r from-slate-800/50 to-slate-700/30 border border-slate-600">
        <div className="text-xs uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1">
          <Icons.Trash size={12} /> {t('metricsDisplay.diagnostics.unusedCode')}
        </div>
        
        {unused.javascript?.items?.length > 0 && (
          <div className="mb-3">
            <div className="text-xs text-amber-400 mb-1 flex items-center gap-1">
              <Icons.Code size={10} /> JavaScript ({formatBytes(unused.javascript.totalWastedBytes)})
            </div>
            {unused.javascript.items.slice(0, 3).map((item, i) => (
              <div key={i} className="text-xs text-slate-400 pl-4 overflow-hidden">
                <span className="break-all">• {truncateUrl(item.url)}</span> <span className="text-amber-400 whitespace-nowrap">({item.wastedPercent?.toFixed(0)}%)</span>
              </div>
            ))}
          </div>
        )}
        
        {unused.css?.items?.length > 0 && (
          <div>
            <div className="text-xs text-pink-400 mb-1 flex items-center gap-1">
              <Icons.Palette size={10} /> CSS ({formatBytes(unused.css.totalWastedBytes)})
            </div>
            {unused.css.items.slice(0, 3).map((item, i) => (
              <div key={i} className="text-xs text-slate-400 pl-4 overflow-hidden">
                <span className="break-all">• {truncateUrl(item.url)}</span> <span className="text-pink-400 whitespace-nowrap">({item.wastedPercent?.toFixed(0)}%)</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderMetricDiagnostics = () => {
    switch (metricKey) {
      case 'lcp':
        return renderLCPDiagnostics();
      case 'cls':
        return renderCLSDiagnostics();
      case 'inp':
        return renderINPDiagnostics();
      case 'ttfb':
        return renderTTFBDiagnostics();
      case 'rtt':
        return renderRTTDiagnostics();
      case 'fcp':
        return renderFCPDiagnostics();
      default:
        return null;
    }
  };

  const isSnapshotSource = recommendationsSource === 'snapshot' || recommendationsSource === 'snapshot-empty';
  const isFreshSource = recommendationsSource === 'fresh';
  const snapshotAgeHours = (() => {
    if (!snapshotFetchedAt) return null;
    const ts = Date.parse(snapshotFetchedAt);
    if (!Number.isFinite(ts)) return null;
    return Math.max(0, Math.round((Date.now() - ts) / (60 * 60 * 1000)));
  })();
  const sourceBadge = (() => {
    if (loading || error || !recommendationsSource) return null;
    if (isSnapshotSource) {
      const ageLabel = snapshotAgeHours == null
        ? null
        : snapshotAgeHours <= 0
          ? (language === 'cs' ? 'před chvílí' : 'just now')
          : (language === 'cs' ? `před ${snapshotAgeHours} h` : `${snapshotAgeHours} h ago`);
      return (
        <span
          className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30"
          title={language === 'cs'
            ? 'Tento výsledek pochází z uložené analýzy. Klikněte na „Přepočítat" pro čerstvé měření.'
            : 'These results come from a stored snapshot. Click "Recompute" for a fresh measurement.'}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
          {language === 'cs' ? 'Z uloženého snapshotu' : 'From stored snapshot'}
          {ageLabel ? ` · ${ageLabel}` : ''}
        </span>
      );
    }
    if (isFreshSource) {
      return (
        <span
          className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
          title={language === 'cs' ? 'Hodnoty pocházejí z čerstvého měření.' : 'Values come from a fresh measurement.'}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          {language === 'cs' ? 'Čerstvě změřeno' : 'Freshly measured'}
        </span>
      );
    }
    return null;
  })();

  const inpCanonicalFinding = String(metric).toUpperCase() === 'INP' && deepINPResults
    ? deriveCanonicalFinding({ metric, language, deepINPResults, device: exportDevice() })
    : null;
  const panelEvidenceContract = useMemo(() => buildVariantPayload().evidenceContract, [
    url, metric, metricValue, strategy, selectedDevice, language, diagnostics,
    deepCLSResults, multiPageResults, deepINPResults, inpCompareResults,
    multiPageINPResults, multiPageLCPResults, deepLCPResults, deepTTFBResults,
    savingsRecommendations, savingsSummary, devicePriority
  ]);
  const panelContent = (
    <>
      <AuditEvidenceContractView contract={panelEvidenceContract} language={language} />
      {inpCanonicalFinding && (
        <section className="mb-4 rounded-lg border border-blue-400/30 bg-blue-950/30 p-4 text-sm text-slate-200">
          <h4 className="mb-2 font-semibold">{language === 'cs' ? 'Důkazy INP — příčina nepotvrzena' : 'INP evidence — cause unconfirmed'}</h4>
          <p>{inpCanonicalFinding.shows}</p>
          <p className="mt-2">{inpCanonicalFinding.cannotDetermine}</p>
          <p className="mt-2">{inpCanonicalFinding.verifyNext}</p>
        </section>
      )}
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-bold text-lg text-orange-400">{t('metricsDisplay.diagnostics.title')}</h4>
            {sourceBadge}
            {recommendationsSource && !loading && !error && (
              <button
                onClick={handleRecompute}
                disabled={loading || isRecomputing}
                className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                title={language === 'cs'
                  ? 'Vynutit přepočet — obejde uložený snapshot a spustí novou analýzu.'
                  : 'Force recompute — bypass the stored snapshot and run a fresh analysis.'}
              >
                {isRecomputing ? (
                  <span className="w-2.5 h-2.5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Icons.RefreshCw size={11} />
                )}
                <span>{language === 'cs' ? 'Přepočítat' : 'Recompute'}</span>
              </button>
            )}
            {recommendationsSource && !loading && !error && (
              <button
                onClick={handleStableMeasure}
                disabled={loading || isRecomputing || isStableMeasuring}
                className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-300 border border-indigo-500/30 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                title={language === 'cs'
                  ? 'Stabilní měření — 5 nezávislých běhů, report použije medián a ukáže rozsah hodnot. Trvá několik minut.'
                  : 'Stable measurement — 5 independent runs, the report uses the median and shows the value range. Takes a few minutes.'}
              >
                {isStableMeasuring ? (
                  <span className="w-2.5 h-2.5 border-2 border-indigo-300 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Icons.Activity size={11} />
                )}
                <span>{language === 'cs' ? 'Medián z 5 běhů' : 'Median of 5 runs'}</span>
              </button>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-1">{t('metricsDisplay.diagnostics.subtitle')}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap justify-end max-w-full">
          <button 
            onClick={handleExportHTML}
            disabled={exporting === 'html'}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 transition-colors disabled:opacity-50 shrink-0"
            title={language === 'cs' ? 'Stáhnout HTML (kompletní diagnostika)' : 'Download HTML (full diagnostics)'}
          >
            {exporting === 'html' ? (
              <div className="w-3 h-3 border-2 border-emerald-300 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icons.Code size={14} />
            )}
            <span className="hidden sm:inline">HTML</span>
          </button>
          <button
            onClick={handleExportMockup}
            disabled={exporting === 'mockup'}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 transition-colors disabled:opacity-50 shrink-0"
            title={language === 'cs' ? 'Stáhnout dashboard report (tmavý styl)' : 'Download dashboard report (dark style)'}
          >
            {exporting === 'mockup' ? (
              <div className="w-3 h-3 border-2 border-cyan-300 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icons.Code size={14} />
            )}
            <span className="hidden sm:inline">Dashboard</span>
          </button>
          <button
            onClick={handleExportAudit}
            disabled={exporting === 'audit'}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 transition-colors disabled:opacity-50 shrink-0"
            title={language === 'cs' ? 'Stáhnout audit report (editorial styl)' : 'Download audit report (editorial style)'}
          >
            {exporting === 'audit' ? (
              <div className="w-3 h-3 border-2 border-amber-300 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icons.Code size={14} />
            )}
            <span className="hidden sm:inline">Audit</span>
          </button>
          <button
            onClick={handleExportUnifiedAudit}
            disabled={exporting === 'cwv'}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-cyan-500/25 to-blue-500/25 hover:from-cyan-500/35 hover:to-blue-500/35 text-cyan-200 border border-cyan-500/40 transition-colors disabled:opacity-50 shrink-0"
            title={language === 'cs'
              ? `Komplexní CWV audit — jeden report se všemi naměřenými hloubkovými vrstvami (${unifiedDeepCount}), headline podle priority #1`
              : `Complete CWV audit — one report with all measured deep layers (${unifiedDeepCount}), headline follows priority #1`}
          >
            {exporting === 'cwv' ? (
              <div className="w-3 h-3 border-2 border-cyan-300 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icons.Zap size={14} />
            )}
            <span className="hidden sm:inline">CWV audit</span>
          </button>
          {user && (
            <div className="relative shrink-0" ref={saveMenuRef}>
              <button
                onClick={() => {
                  if (
                    exporting === 'cloud' || exporting === 'cloud-waiting'
                    || exporting === 'cloud-done' || exporting === 'cloud-bulk'
                    || exporting === 'cloud-partial'
                  ) return;
                  setShowSaveMenu(v => !v);
                }}
                disabled={
                  exporting === 'cloud' || exporting === 'cloud-waiting'
                  || exporting === 'cloud-done' || exporting === 'cloud-bulk'
                  || exporting === 'cloud-partial'
                }
                className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-70 ${
                  exporting === 'cloud-done'
                    ? 'bg-green-500/20 text-green-300'
                    : exporting === 'cloud-partial'
                      ? 'bg-orange-500/20 text-orange-300'
                      : exporting === 'cloud-waiting'
                        ? 'bg-amber-500/20 text-amber-300'
                        : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-300'
                }`}
                title={
                  exporting === 'cloud-waiting'
                    ? (language === 'cs' ? 'Čekám na dopočet odhadů úspory…' : 'Waiting for savings estimates…')
                    : exporting === 'cloud-bulk'
                      ? (language === 'cs'
                          ? `Ukládám variantu ${bulkProgress?.current || 0}/${bulkProgress?.total || 3}…`
                          : `Saving variant ${bulkProgress?.current || 0}/${bulkProgress?.total || 3}…`)
                      : exporting === 'cloud-partial'
                        ? (language === 'cs'
                            ? `Uloženo s chybou (${bulkProgress?.failed?.length || 0} z ${bulkProgress?.total || 0} variant selhalo)`
                            : `Saved with errors (${bulkProgress?.failed?.length || 0} of ${bulkProgress?.total || 0} variants failed)`)
                        : (language === 'cs' ? 'Uložit report do cloudu' : 'Save report to cloud')
                }
                aria-haspopup="menu"
                aria-expanded={showSaveMenu}
              >
                {exporting === 'cloud' || exporting === 'cloud-bulk' ? (
                  <div className="w-3 h-3 border-2 border-blue-300 border-t-transparent rounded-full animate-spin" />
                ) : exporting === 'cloud-waiting' ? (
                  <div className="w-3 h-3 border-2 border-amber-300 border-t-transparent rounded-full animate-spin" />
                ) : exporting === 'cloud-done' ? (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : exporting === 'cloud-partial' ? (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                )}
                <span className="hidden sm:inline">{
                  exporting === 'cloud-done'
                    ? (language === 'cs'
                        ? (bulkProgress ? `Uloženo ${bulkProgress.total}/${bulkProgress.total}` : 'Uloženo')
                        : (bulkProgress ? `Saved ${bulkProgress.total}/${bulkProgress.total}` : 'Saved'))
                    : exporting === 'cloud-partial'
                      ? (language === 'cs'
                          ? `Uloženo ${(bulkProgress?.total || 0) - (bulkProgress?.failed?.length || 0)}/${bulkProgress?.total || 0}`
                          : `Saved ${(bulkProgress?.total || 0) - (bulkProgress?.failed?.length || 0)}/${bulkProgress?.total || 0}`)
                      : exporting === 'cloud-waiting'
                        ? (language === 'cs' ? 'Čekám na diagnostiku…' : 'Waiting for diagnostics…')
                        : exporting === 'cloud-bulk'
                          ? (language === 'cs'
                              ? `Ukládám ${bulkProgress?.current || 0}/${bulkProgress?.total || 3}…`
                              : `Saving ${bulkProgress?.current || 0}/${bulkProgress?.total || 3}…`)
                          : exporting === 'cloud'
                            ? (language === 'cs' ? 'Ukládám…' : 'Saving…')
                            : (language === 'cs' ? 'Uložit' : 'Save')
                }</span>
                {!exporting && (
                  <svg className="w-3 h-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </button>
              {showSaveMenu && !exporting && (
                <div
                  role="menu"
                  className="absolute right-0 top-full mt-1.5 z-50 min-w-[240px] rounded-lg bg-slate-900 border border-slate-700 shadow-xl overflow-hidden"
                >
                  <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                    {language === 'cs' ? 'Uložit jako variantu' : 'Save as variant'}
                  </div>
                  <button
                    onClick={() => handleSaveHTMLToCloud('standard')}
                    className="w-full text-left px-3 py-2 text-xs text-emerald-200 hover:bg-emerald-500/10 flex items-center gap-2"
                    role="menuitem"
                  >
                    <Icons.Code size={14} className="text-emerald-300" />
                    <span>{language === 'cs' ? 'Standardní (kompletní diagnostika)' : 'Standard (full diagnostics)'}</span>
                  </button>
                  <button
                    onClick={() => handleSaveHTMLToCloud('dashboard')}
                    className="w-full text-left px-3 py-2 text-xs text-cyan-200 hover:bg-cyan-500/10 flex items-center gap-2"
                    role="menuitem"
                  >
                    <Icons.Code size={14} className="text-cyan-300" />
                    <span>{language === 'cs' ? 'Dashboard (tmavý styl)' : 'Dashboard (dark style)'}</span>
                  </button>
                  <button
                    onClick={() => handleSaveHTMLToCloud('audit')}
                    className="w-full text-left px-3 py-2 text-xs text-amber-200 hover:bg-amber-500/10 flex items-center gap-2"
                    role="menuitem"
                  >
                    <Icons.Code size={14} className="text-amber-300" />
                    <span>{language === 'cs' ? 'Audit (editorial styl)' : 'Audit (editorial style)'}</span>
                  </button>
                  <div className="border-t border-slate-800" />
                  <button
                    onClick={() => handleSaveAllVariantsToCloud()}
                    className="w-full text-left px-3 py-2 text-xs text-blue-100 hover:bg-blue-500/10 flex items-center gap-2 font-medium"
                    role="menuitem"
                  >
                    <Icons.Code size={14} className="text-blue-300" />
                    <span>{language === 'cs' ? 'Všechny varianty (Standard + Dashboard + Audit)' : 'All variants (Standard + Dashboard + Audit)'}</span>
                  </button>
                </div>
              )}
            </div>
          )}
          <button 
            onClick={() => setIsFullscreen(!isFullscreen)} 
            className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-700 transition-colors shrink-0"
            title={isFullscreen ? (language === 'cs' ? 'Zmenšit' : 'Minimize') : (language === 'cs' ? 'Celá obrazovka' : 'Fullscreen')}
          >
            {isFullscreen ? <Icons.Minimize size={18} /> : <Icons.Maximize size={18} />}
          </button>
          <button 
            onClick={() => { setIsFullscreen(false); onClose(); }} 
            className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-700 transition-colors shrink-0"
            title={language === 'cs' ? 'Zavřít' : 'Close'}
          >
            <Icons.Close size={18} />
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-3 text-slate-400">{t('metricsDisplay.diagnostics.loading')}</span>
        </div>
      )}

      {error && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          <Icons.AlertTriangle className="inline mr-2" size={16} />
          {t('metricsDisplay.diagnostics.error')}
        </div>
      )}

      {!loading && !error && diagnostics && (
        <div className="space-y-4">
          {user && <DevicePriorityRanking url={url} onData={setDevicePriority} />}

          {renderMetricDiagnostics()}
          
          {(metricKey === 'lcp' || metricKey === 'fcp') && renderRenderBlockingResources()}
          
          {(metricKey === 'lcp' || metricKey === 'inp' || metricKey === 'fcp') && renderUnusedResources()}
          
          <W3CPerformanceInsights 
            diagnostics={diagnostics} 
            showDeviceCapabilities={metricKey === 'inp' || metricKey === 'lcp'}
          />
          
          <AdvancedDiagnostics diagnostics={diagnostics} currentUrl={url} />
          
          {!metricData && !diagnostics.serverTiming && !diagnostics.longTasks?.count && (
            <div className="text-center py-8 text-slate-400">
              <Icons.Search className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>{t('metricsDisplay.diagnostics.noIssues')}</p>
            </div>
          )}
        </div>
      )}
    </>
  );

  const UpgradeModal = () => showUpgradeModal && (
    <div className="cv-app-portal fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowUpgradeModal(false)}>
      <div className="bg-slate-900 border-2 border-purple-500/50 rounded-2xl p-6 max-w-md mx-4 shadow-2xl shadow-purple-500/20" onClick={e => e.stopPropagation()}>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Icons.Zap size={32} className="text-white" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">
            {language === 'cs' ? 'Hloubková analýza' : 'Deep Analysis'}
          </h3>
          <p className="text-slate-400 mb-4">
            {language === 'cs' 
              ? 'Hloubkové analýzy jsou dostupné pouze pro tarify Premium a Enterprise.'
              : 'Deep analyses are available only for Premium and Enterprise plans.'}
          </p>
          <div className="space-y-2">
            <a 
              href="/pricing" 
              className="block w-full py-3 px-4 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium transition-all"
            >
              {language === 'cs' ? 'Zobrazit tarify' : 'View Plans'}
            </a>
            <button 
              onClick={() => setShowUpgradeModal(false)}
              className="w-full py-2 px-4 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm transition-all"
            >
              {language === 'cs' ? 'Zavřít' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (isFullscreen) {
    return createPortal(
      <>
        <UpgradeModal />
        <div className="cv-app-portal fixed inset-0 z-[9999] bg-slate-950/98 backdrop-blur-sm overflow-auto">
          <div className="max-w-5xl mx-auto p-6 py-8">
            <div ref={panelRef} data-export-container className="bg-slate-900 rounded-xl border-2 border-orange-500/40 shadow-2xl shadow-orange-500/20 p-6">
              {panelContent}
            </div>
          </div>
        </div>
      </>,
      document.body
    );
  }

  return (
    <>
      <UpgradeModal />
      <div ref={panelRef} data-export-container className="mt-4 p-5 rounded-xl bg-slate-900 border-2 border-orange-500/40 shadow-lg shadow-orange-500/10 animate-slide-in scroll-mt-36">
        {panelContent}
      </div>
    </>
  );
};

export default DiagnosticsPanel;
